import { use, useCallback, useEffect, useRef } from 'react';

import { FormContext } from '../Form/FormContext';

interface FormValidityObserverProps {
  onValidityChange?: (isValid: boolean) => void;
  isExtraValid?: boolean;
  formId?: string;
  validateForm?: (form: HTMLElement) => boolean;
  debounceMs?: number;
}

export function FormValidityObserver({
  onValidityChange,
  isExtraValid = true,
  formId,
  validateForm,
  debounceMs = 100,
}: FormValidityObserverProps) {
  const { valid } = use(FormContext);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkFormValidity = useCallback(() => {
    const formValid =
      validateForm && formId
        ? (() => {
            const form = document.getElementById(formId);
            return form ? validateForm(form) : false;
          })()
        : true;

    onValidityChange?.(valid && isExtraValid && formValid);
  }, [formId, isExtraValid, onValidityChange, valid, validateForm]);

  useEffect(() => {
    checkFormValidity();
  }, [checkFormValidity]);

  useEffect(() => {
    if (!validateForm || !formId) {
      return;
    }

    const form = document.getElementById(formId);
    if (!form) {
      return;
    }

    const handleInputChange = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(checkFormValidity, debounceMs);
    };

    form.addEventListener('input', handleInputChange);
    form.addEventListener('change', handleInputChange);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      form.removeEventListener('input', handleInputChange);
      form.removeEventListener('change', handleInputChange);
    };
  }, [checkFormValidity, debounceMs, formId, validateForm]);

  return null;
}
